/**
 * Universal upload client.
 *
 * Handles both small (< 100 MB, single presigned PUT) and large (multipart) uploads.
 * Calls backend /api/uploads/* endpoints for coordination.
 * Uploads file bytes directly to S3 via presigned URLs — never through the app server.
 *
 * Usage:
 *   const asset = await uploadFile(file, projectId, apiInstance, onProgress, abortSignal);
 */

/**
 * Upload a single file.
 *
 * @param {File} file - Browser File object
 * @param {string|null} projectId - Optional project to attach to
 * @param {import('axios').AxiosInstance} api - Authenticated axios instance
 * @param {(pct: number, phase: string) => void} onProgress
 * @param {AbortSignal} [signal]
 * @returns {Promise<object>} - The Asset object from the backend
 */
export async function attachImportedSource(asset, api) {
  return (await api.post('/uploads/intake/' + asset._id + '/attach')).data?.asset;
}

export async function uploadFile(file, projectId, api, onProgress, signal, packageFiles, intake) {
  if (!file) throw new Error("No file provided");

  const report = (pct, phase) => onProgress?.(Math.min(100, Math.round(pct)), phase);

  report(0, "initiating");

  // ── 1. Initiate upload ────────────────────────────────────────────────────
  const { data: init } = await api.post("/uploads/initiate", {
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || "application/octet-stream",
    projectId: projectId || undefined,
    ...(packageFiles?.length ? { packageFiles } : {}),
    ...(intake ? { intakeId: intake.id, packageManifest: intake.companions || [] } : {}),
  });

  if (init.alreadyCompleted && init.asset?._id) return init.asset;

  const { assetId, key, useMultipart, uploadId } = init;
  if (intake && init.transferComplete) {
    report(97, 'completing');
    return (await api.post('/uploads/complete', { assetId, key, uploadId, reconcile: true })).data.asset;
  }

  try {
    if (!useMultipart) {
      // ── Small file: single PUT ──────────────────────────────────────────
      report(0, 'uploading');
      await putToS3(init.presignedUrl, file, file.type || "application/octet-stream", (pct) => {
        report(pct * 0.95, "uploading");
      }, signal);

      report(97, "completing");
      const { data } = await api.post("/uploads/complete", { assetId, key });
      report(100, "done");
      return data.asset;
    }

    // ── Large file: multipart ────────────────────────────────────────────
    const { partSize, totalParts } = init;
    const parts = [];

    for (let i = 0; i < totalParts; i++) {
      if (signal?.aborted) throw new Error("Upload cancelled");

      const start  = i * partSize;
      const end    = Math.min(start + partSize, file.size);
      const chunk  = file.slice(start, end);
      const partNo = i + 1;
      const previous = intake && init.uploadedParts?.find(part => part.PartNumber === partNo && part.Size === end - start && part.ETag);
      if (previous) { parts.push({ ETag: previous.ETag, PartNumber: partNo }); report(((i + 1) / totalParts) * 90, 'uploading'); continue; }

      // Get signed URL for this part
      report((i / totalParts) * 90, 'signing upload part');
      const { data: partData } = await api.post("/uploads/sign-part", {
        uploadId,
        key,
        partNumber: partNo,
      });

      // Upload the chunk directly to S3
      report((i / totalParts) * 90, 'uploading');
      const etag = await putPartToS3(partData.url, chunk, (pct) => {
        const overall = ((i + pct / 100) / totalParts) * 90;
        report(overall, "uploading");
      }, signal);

      parts.push({ ETag: etag, PartNumber: partNo });
    }

    report(92, "completing");
    const { data } = await api.post("/uploads/complete", { assetId, key, uploadId, parts });
    report(100, "done");
    return data.asset;

  } catch (err) {
    // Best-effort abort — don't let this throw over the original error
    if (uploadId && !intake) {
      api.post("/uploads/abort", { uploadId, key, assetId }).catch(() => {});
    }
    throw err;
  }
}

/**
 * Upload a model package: root file + companion files (MTL, textures, .bin, etc.)
 * All companions are placed under the root asset's S3 folder.
 * Returns the root Asset object (with metadata.modelPackage populated).
 *
 * @param {File} rootFile
 * @param {File[]} companions
 * @param {string|null} projectId
 * @param {import('axios').AxiosInstance} api
 * @param {(pct: number, phase: string) => void} onProgress
 */
export async function uploadPackage(rootFile, companions, projectId, api, onProgress, intake) {
  const report     = (pct, phase) => onProgress?.(Math.min(100, Math.round(pct)), phase);
  const totalSlots = 1 + companions.length;

  // 1. Upload the root model file via the standard flow
  report(0, "uploading root");
  const rootAsset    = await uploadFile(rootFile, projectId, api, (pct) =>
    report(pct / totalSlots, "uploading root"), undefined, companions.map(file => file.name), intake);
  const rootAssetId  = String(rootAsset._id);

  if (!companions.length) return rootAsset;
  if (intake && rootAsset.metadata?.modelPackage && rootAsset.metadata?.packageImport?.status !== 'incomplete' && !rootAsset.metadata?.packageRegistrationFailed) return rootAsset;

  const registered = [];

  // 2. Upload each companion directly to the root asset's S3 folder
  for (let i = 0; i < companions.length; i++) {
    const cf          = companions[i];
    const slotBase    = (i + 1) / totalSlots;
    try {
      const { data: init } = await api.post("/uploads/companion", {
        rootAssetId,
        fileName:  cf.name,
        fileSize:  cf.size,
        mimeType:  cf.type || "application/octet-stream",
      });

      if (!init.alreadyUploaded) await putToS3Companion(init.presignedUrl, cf, cf.type || "application/octet-stream",
        (pct) => report((slotBase + pct / 100 / totalSlots) * 95, `uploading ${cf.name}`));

      registered.push({ fileName: init.fileName, relativePath:init.relativePath || cf.name, s3Key: init.key,
        mimeType: cf.type || "", sizeBytes: cf.size });
    } catch (cause) {
      throw Object.assign(new Error('The source is stored, but a companion upload did not finish. Check or repair the stored Asset before uploading again.'), { code: 'PACKAGE_INCOMPLETE', storedAsset: rootAsset, cause });
    }
  }

  // 3. Register companion metadata on the root asset — retry up to 3× with backoff
  if (registered.length) {
    let lastRegErr;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        report(97, "registering package");
        const { data } = await api.post("/uploads/register-package",
          { rootAssetId, companions: registered });
        report(100, "done");
        if (String(data.asset?._id) !== rootAssetId) throw new Error('Package registration returned a different Asset.');
        return data.asset;
      } catch (err) {
        lastRegErr = err;
        if (attempt < 3) await new Promise(r => setTimeout(r, attempt * 1000));
      }
    }
    // All retries exhausted — flag the asset so the frontend can trigger repair
    console.warn("[uploadPackage] register-package failed after 3 attempts:", lastRegErr?.message);
    throw Object.assign(new Error('The source was stored, but package registration did not finish. Repair this stored Asset without uploading its source again.'), { code: 'PACKAGE_INCOMPLETE', storedAsset: rootAsset, cause: lastRegErr });
  }

  report(100, "done");
  return rootAsset;
}

/**
 * PUT companion file bytes to a presigned S3 URL with optional progress.
 */
function putToS3Companion(url, file, contentType, onPct) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onPct((e.loaded / e.total) * 100); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(Object.assign(new Error(`S3 PUT failed: HTTP ${xhr.status}`), {status:xhr.status}));
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.send(file);
  });
}

/**
 * PUT a whole file to a presigned S3 URL with progress tracking.
 * Returns nothing (single-part PUT has no ETag we need).
 */
function putToS3(url, file, contentType, onPct, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onPct((e.loaded / e.total) * 100);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(Object.assign(new Error(`S3 PUT failed: HTTP ${xhr.status}`), {status:xhr.status}));
    };

    xhr.onerror = () => reject(new Error("Network error during upload"));

    if (signal) {
      signal.addEventListener("abort", () => {
        xhr.abort();
        reject(Object.assign(new Error("Upload cancelled"), {code:'ERR_CANCELED'}));
      });
    }

    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.send(file);
  });
}

/**
 * PUT one multipart chunk to S3, returning the ETag from the response header.
 */
function putPartToS3(url, blob, onPct, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onPct((e.loaded / e.total) * 100);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader("ETag") || "";
        if (!etag) { reject(Object.assign(new Error('Storage did not expose the uploaded part ETag.'), {code:'UPLOAD_ETAG_UNAVAILABLE'})); return; }
        resolve(etag.replace(/"/g, "")); // strip surrounding quotes
      } else {
        reject(Object.assign(new Error(`S3 part PUT failed: HTTP ${xhr.status}`), {status:xhr.status}));
      }
    };

    xhr.onerror = () => reject(new Error("Network error during part upload"));

    if (signal) {
      signal.addEventListener("abort", () => {
        xhr.abort();
        reject(Object.assign(new Error("Upload cancelled"), {code:'ERR_CANCELED'}));
      });
    }

    xhr.open("PUT", url);
    xhr.send(blob);
  });
}
